import { getChainDisplayName } from '@passkeys/assets';
import Box from '@passkeys/ui/box';
import CopyableAddress from '@passkeys/ui/copyable-address';
import Icon from '@passkeys/ui/icon';
import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import { useCallback } from 'react';
import QRCode from 'react-qr-code';
import invariant from 'tiny-invariant';
import { useParams } from 'wouter';

import Subpage from '@/ui/components/subpage';
import exodus from '@/ui/exodus';
import useAsset from '@/ui/hooks/use-asset';
import useExpandedEffect from '@/ui/hooks/use-expanded-effect';
import useReceiveAddress from '@/ui/hooks/use-receive-address';
import { openInBlankWindow } from '@/ui/utils/navigation.js';
import FramedChainIcon from './components/framed-chain-icon.js';

// IMPORTANT: Do not change the size of the QR without checking if it still reads.
// There's a safe proportion between the size of the QR and the of the asset icon.
const QR_CODE_SIZE = 188;

// TODO: This is actually the "ChainReceivePage", should we move and/or rename it?
function TokenReceivePage() {
  const { token } = useParams<{ token: string }>();

  invariant(token, "TokenReceivePage: Missing 'token' param");

  // TODO: Decide where to trace boundary between legacy terminology ("asset")
  // and new terminology ("token").
  const assetName = token;

  const asset = useAsset(assetName);
  if (!asset) {
    throw new Error(`Invalid asset ${assetName}`);
  }

  const assetDisplayName = getChainDisplayName(asset);

  const { address } = useReceiveAddress({ assetName });

  const handleAddressCopy = useCallback(() => {
    exodus.analytics.track({
      event: 'WalletAssetCopyReceiveAddress',
      properties: {
        assetName: asset.name,
        network: asset.baseAssetName,
      },
    });
  }, [asset]);

  const handleViewOnExplorerClick = useCallback(async () => {
    const url = await asset.blockExplorer.addressUrl(address!);
    openInBlankWindow(url);
  }, [address, asset.blockExplorer]);

  useExpandedEffect(() => {
    exodus.analytics.track({
      event: 'WalletAssetReceiveView',
      properties: {
        assetName: asset.name,
        network: asset.baseAssetName,
      },
    });
  }, [asset]);

  const loading = !asset || !address;

  return (
    <Subpage title="Receive" className="flex flex-col items-center pb-8">
      <Box
        className="relative mb-10 mt-8 flex size-[240px] items-center justify-center"
        size="huge"
      >
        {!loading && (
          <FramedChainIcon
            className="!absolute inset-1/2 !z-10 translate-x-[-50%] translate-y-[-50%]"
            name={assetName}
            displayName={assetDisplayName}
          />
        )}
        {loading ? (
          <Skeleton
            height={QR_CODE_SIZE}
            width={QR_CODE_SIZE}
            borderRadius={12}
          />
        ) : (
          <QRCode
            value={address}
            size={QR_CODE_SIZE}
            bgColor="transparent"
            fgColor="var(--qr-fg-color)"
            className="[--qr-fg-color:theme(colors.black)] theme-dark:[--qr-fg-color:theme(colors.white)]"
          />
        )}
      </Box>

      <Text className="mb-4 text-center">Your {assetDisplayName} address</Text>

      {loading ? (
        <Skeleton height={50} width={296} />
      ) : (
        <CopyableAddress
          address={address}
          chain={assetName}
          chainDisplayName={assetDisplayName}
          className="mx-6"
          onCopy={handleAddressCopy}
        />
      )}

      <Text
        size={14}
        align="center"
        className="mt-4 text-black/40 theme-dark:text-neutral-400"
      >
        Transactions may take a few minutes to complete.
        <br />
        See the status on the web:
      </Text>

      <button
        className="inline-flex h-10 items-center justify-center gap-x-2 font-mono text-[12px] font-medium uppercase leading-none text-black theme-dark:text-white"
        disabled={!address}
        onClick={handleViewOnExplorerClick}
        type="button"
      >
        View on explorer
        <Icon name="arrow-up-right" size={12} />
      </button>
    </Subpage>
  );
}

export default TokenReceivePage;
